import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API, formatApiErrorDetail } from "../App";
import AppShell from "./AppShell";
import axios from "axios";
import { 
  Plus, Users,
  Edit, Trash2, X, Search
} from "lucide-react";

const Clients = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    address: "",
    city: "",
    country: "",
    phone: ""
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API}/clients`);
      setClients(data);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || "",
        email: client.email || "",
        company: client.company || "",
        address: client.address || "",
        city: client.city || "",
        country: client.country || "",
        phone: client.phone || ""
      });
    } else {
      setEditingClient(null);
      setFormData({
        name: "",
        email: "",
        company: "",
        address: "",
        city: "",
        country: "",
        phone: ""
      });
    }
    setError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClient(null);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
      } else {
        await axios.post(`${API}/clients`, formData);
      }
      await fetchClients();
      closeModal();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Are you sure you want to delete this client?")) return;

    try {
      await axios.delete(`${API}/clients/${clientId}`);
      await fetchClients();
    } catch (err) {
      console.error("Failed to delete client:", err);
    }
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AppShell active="clients">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <div>
          <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">
            Clients
          </h1>
          <p className="text-[13px] sm:text-[14px] text-[#6B7280] mt-1">Manage your client contacts</p>
        </div>
        <button
          onClick={() => openModal()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-client-btn"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add client</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <input
            type="text"
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-11 text-[14px] w-full"
            data-testid="search-clients"
          />
        </div>
      </div>

      {/* Clients List */}
      <div className="bg-white border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <p className="text-[14px] text-[#6B7280]">Loading clients...</p>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="p-12 sm:p-16 text-center">
            <div className="w-12 h-12 bg-[#F3F4F6] flex items-center justify-center mx-auto mb-4">
              <Users className="w-6 h-6 text-[#9CA3AF]" />
            </div>
            <h3 className="font-semibold text-[17px] text-[#0A0A0A] mb-2">No clients yet</h3>
            <p className="text-[14px] text-[#6B7280] mb-6">Add your first client to get started</p>
            <button
              onClick={() => openModal()}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="empty-add-client"
            >
              <Plus className="w-4 h-4" />
              Add client
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="w-full hidden sm:table">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold text-[12px] text-[#6B7280] uppercase tracking-wide">Name</th>
                  <th className="text-left px-5 py-3 font-semibold text-[12px] text-[#6B7280] uppercase tracking-wide">Email</th>
                  <th className="text-left px-5 py-3 font-semibold text-[12px] text-[#6B7280] uppercase tracking-wide">Company</th>
                  <th className="text-left px-5 py-3 font-semibold text-[12px] text-[#6B7280] uppercase tracking-wide">Location</th>
                  <th className="px-5 py-3 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.map((client) => (
                  <tr 
                    key={client.id} 
                    className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
                    data-testid={`client-row-${client.id}`}
                  >
                    <td className="px-5 py-4">
                      <span className="font-semibold text-[14px] text-[#0A0A0A]">{client.name}</span>
                    </td>
                    <td className="px-5 py-4 text-[14px] text-[#6B7280]">
                      {client.email}
                    </td>
                    <td className="px-5 py-4 text-[14px] text-[#6B7280]">
                      {client.company || "—"}
                    </td>
                    <td className="px-5 py-4 text-[14px] text-[#6B7280]">
                      {[client.city, client.country].filter(Boolean).join(", ") || "—"}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openModal(client)}
                          className="p-2 hover:bg-[#F3F4F6] transition-colors"
                          title="Edit"
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Edit className="w-4 h-4 text-[#6B7280]" />
                        </button>
                        <button
                          onClick={() => handleDelete(client.id)}
                          className="p-2 hover:bg-[#FEF2F2] text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
                          title="Delete"
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card List */}
            <div className="sm:hidden divide-y divide-[#E5E7EB]">
              {filteredClients.map((client) => (
                <div
                  key={client.id}
                  className="p-4"
                  data-testid={`client-card-${client.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[14px] text-[#0A0A0A]">{client.name}</p>
                      <p className="text-[13px] text-[#6B7280] truncate">{client.email}</p>
                      {client.company && <p className="text-[12px] text-[#9CA3AF] mt-0.5">{client.company}</p>}
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => openModal(client)}
                        className="p-2 hover:bg-[#F3F4F6] transition-colors"
                        data-testid={`edit-client-mobile-${client.id}`}
                      >
                        <Edit className="w-4 h-4 text-[#6B7280]" />
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="p-2 hover:bg-[#FEF2F2] text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
                        data-testid={`delete-client-mobile-${client.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-[#E5E7EB] shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b border-[#E5E7EB] flex items-center justify-between">
              <h2 className="font-semibold text-[17px] text-[#0A0A0A]">
                {editingClient ? "Edit client" : "Add client"}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-[#F3F4F6] transition-colors"
                data-testid="close-modal"
              >
                <X className="w-4 h-4 text-[#6B7280]" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {error && (
                <div className="p-4 bg-[#FEF2F2] border border-[#FECACA] text-[#DC2626] text-[14px]">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="input text-[14px]"
                  required
                  data-testid="client-name-input"
                />
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="input text-[14px]"
                  required
                  data-testid="client-email-input"
                />
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Company</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                  className="input text-[14px]"
                  data-testid="client-company-input"
                />
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="input text-[14px]"
                  data-testid="client-address-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="input text-[14px]"
                    data-testid="client-city-input"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Country</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                    className="input text-[14px]"
                    data-testid="client-country-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="input text-[14px]"
                  data-testid="client-phone-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 btn-outline"
                  data-testid="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 btn-primary disabled:opacity-50"
                  data-testid="save-client-btn"
                >
                  {saving ? "Saving..." : (editingClient ? "Update" : "Add client")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default Clients;
